import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Truck,
  Star,
  Calendar,
  DollarSign,
  Search,
  User,
  Package,
  BarChart3,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { getCarriers, Carrier } from "@/api/broker/getCarriers";
import { CarriersList } from "./CarriersList";
import { CarrierInfo } from "./CarrierInfo";
import { CarrierLoadHistory } from "./CarrierLoadHistory";
import { CarrierManagementSkeleton } from "./skeletons";

export default function CarrierManagement() {
  const { data: carriers, isLoading } = useQuery({
    queryKey: ['carriers'],
    queryFn: () => getCarriers(),
  });

  console.log(carriers);
  
  // Initialize as undefined, not carriers?.[0]
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | undefined>(undefined);

  // Only set selectedCarrier when carriers data is actually available
  useEffect(() => {
    if (carriers && carriers.length > 0 && !selectedCarrier) {
      setSelectedCarrier(carriers[0]);
    }
  }, [carriers, selectedCarrier]);

  if (isLoading) {
    return <CarrierManagementSkeleton />;
  }

  return (
    <div className="min-h-screen rounded-xl">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
        {/* Left Panel - Carrier List */}
        <CarriersList 
          carriers={carriers || []} 
          selectedCarrier={selectedCarrier} 
          setSelectedCarrier={setSelectedCarrier}
        />

        {/* Right Panel - Carrier Details */}
        <div className="lg:col-span-2 m-4">
          {selectedCarrier ? (
            <div className="space-y-6">
              {/* Carrier Header */}
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-center">
                    <div>
                      <CardTitle className="text-2xl">
                        {selectedCarrier.companyName}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Tabbed Content */}
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="overview">Overview & Contact</TabsTrigger>
                  <TabsTrigger value="loads">Load History</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                  <CarrierInfo selectedCarrier={selectedCarrier}/>
                </TabsContent>

                <TabsContent value="loads" className="space-y-6">
                  <CarrierLoadHistory selectedCarrier={selectedCarrier}/>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <Card className="h-full flex items-center justify-center">
              <CardContent>
                <div className="text-center text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a carrier to view details</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}